import { debounceTime } from "rxjs";
import { TranslateModule } from "@ngx-translate/core";
import { NgTemplateOutlet } from "@angular/common";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { MatFormField, MatInputModule } from "@angular/material/input";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from "@angular/forms";
import {
  Component,
  effect,
  forwardRef,
  HostListener,
  inject,
  Injector,
  runInInjectionContext,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation,
  input,
  output,
  viewChild,
} from "@angular/core";

import { IsEnglishDirective } from "../../directives";
import { ReactiveFormsBaseComponent } from "../../base-components";
import { ValidationHandlerPipe, LocalizedNamePipe } from "../../pipes";

import { Dropdown } from "src/app/core";

@Component({
  selector: "app-input-text-dropdown",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    TranslateModule,
    NgTemplateOutlet,
    IsEnglishDirective,
    ValidationHandlerPipe,
    LocalizedNamePipe,
  ],
  templateUrl: "./input-text-dropdown.component.html",
  styleUrl: "./input-text-dropdown.component.scss",
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTextDropdownComponent),
      multi: true,
    },
  ],
})
export class InputTextDropdownComponent extends ReactiveFormsBaseComponent {
  private injector = inject(Injector);

  readonly label = input.required<string>();
  readonly matErrorClass = input("");
  readonly placeholder = input("");
  readonly contentType = input<"text" | "number">("number");

  readonly options = input.required<Dropdown[]>();
  readonly dropdownFormControlName = input.required<string>();
  readonly withSearch = input(false);
  readonly bindValue = input<keyof Dropdown>("id");
  readonly dropdownSelectedValueRef = input<TemplateRef<unknown>>();

  readonly readonly = input(false);

  readonly inputValue = output<string>();
  readonly selected = output<Dropdown["id"]>();

  readonly matFormField = viewChild.required<MatFormField>("matFormField");

  filterControl = new FormControl<string | null>(null);
  filteredOptions: Dropdown[] = [];
  isEnglish = true;

  get dropdownControl() {
    return this.form.get(this.dropdownFormControlName()) as FormControl;
  }

  get dropdownValue() {
    if (!this.dropdownControl) return null;
    return this.dropdownControl.value;
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    runInInjectionContext(this.injector, () => {
      this.handleDropdownControlSearch();
      this.handleDropdownControlErrorState();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    //Add '${implements OnChanges}' to the class.
    if (changes["options"]) this.filteredOptions = changes["options"].currentValue;
  }

  onKeyPress(event: KeyboardEvent): void {
    if (this.contentType() === "number" && ["e", "+", "-"].includes(event.key.toLowerCase())) {
      event.preventDefault();
    }
  }

  getSelectedValue(event: { source: MatSelect; value: Dropdown["id"] }) {
    this.selected.emit(event.value);
    this.control.markAsTouched();
    this.control.updateValueAndValidity();
  }

  compareWithFn = (valueOption: number, dropdownOption: number) => {
    return valueOption == dropdownOption;
  };

  private handleDropdownControlSearch() {
    if (this.withSearch()) {
      this.filterControl.valueChanges.pipe(debounceTime(500), takeUntilDestroyed()).subscribe((searchKey) => {
        if (!searchKey?.length) {
          this.filteredOptions = this.options();
          return;
        }

        this.filteredOptions = this.options().filter(
          (each) =>
            each.nameEn.toLowerCase().includes(searchKey.toLowerCase()) || each.nameAr.toLowerCase().includes(searchKey.toLowerCase()),
        );
      });
    }
  }

  private handleDropdownControlErrorState() {
    const dropdownControlStatusChanges = toSignal(this.dropdownControl?.statusChanges);
    const InputTextControlValueChanges = toSignal(this.control?.valueChanges);

    effect(() => {
      const elementRef: HTMLElement = this.matFormField()._elementRef.nativeElement;
      const isControlInValid = this.control.invalid;
      const isDropdownControlInValid = this.dropdownControl.invalid;

      if (
        (InputTextControlValueChanges() && isControlInValid && this.control.touched) ||
        (isDropdownControlInValid && this.dropdownControl.touched)
      ) {
        elementRef.querySelector(".mat-mdc-text-field-wrapper")?.classList.add("mdc-text-field--invalid");
        if (!this.dropdownControl.hasValidator(Validators.required)) {
          this.dropdownControl.setValidators(Validators.required);
          this.dropdownControl.updateValueAndValidity();
        }
      } else {
        elementRef.querySelector(".mat-mdc-text-field-wrapper")?.classList.remove("mdc-text-field--invalid");
      }
    });
  }

  @HostListener("paste", ["$event"]) onPaste(event: ClipboardEvent): void {
    if (this.contentType() === "number" && ["e", "+", "-"].includes(event.clipboardData?.getData("text/plain").toLowerCase()!)) {
      event.preventDefault();
    }
  }
}
